import {createSelector} from 'reselect'
import isEmpty from 'lodash/isEmpty.js'
import round from 'lodash/round.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import {isNumeric} from '../../../common/utils.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import CurrencyInput from '../../../common/components/CurrencyInput.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {orderSelector, saveFinancialInfo} from '../../../data/orders.js'
import Currency from '../../../common/components/Currency.js'
import AutoCalculateOrderTotalsCheckbox, {
  autoCalculateOrderTotalsSelector,
} from '../AutoCalculateOrderTotalsCheckbox.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'

export const MODAL_FORM = 'ORDER_TOTALS_MODAL'

export function showOrderTotalsModalModal(
  orderNumber,
  autoFocusOn = 'product_amount',
) {
  const {financial} = orderSelector(getState(), {orderNumber})

  setForm(MODAL_FORM, {
    orderNumber,
    product_amount: String(financial.product_amount),
    discount_amount: String(financial.discount_amount),
    shipping_amount: String(financial.shipping_amount),
    tax_amount: String(financial.tax_amount),
    grand_total: String(financial.grand_total),
    autoFocusOn,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  return updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  return removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  autoCalculateOrderTotalsSelector,
  (
    {product_amount, discount_amount, shipping_amount, tax_amount, grand_total},
    autoCalculateOrderTotals,
  ) => {
    const errors = {}

    if (!autoCalculateOrderTotals && !isNumeric(product_amount)) {
      errors.product_amount = 'Product Total must be a number'
      errors.preventSave = true
    }

    if (!isNumeric(discount_amount)) {
      errors.discount_amount = 'Discount must be a number'
      errors.preventSave = true
    }

    if (!isNumeric(shipping_amount)) {
      errors.shipping_amount = 'Shipping and Handling must be a number'
      errors.preventSave = true
    }

    if (!isNumeric(tax_amount)) {
      errors.tax_amount = 'Tax must be a number'
      errors.preventSave = true
    }

    if (!autoCalculateOrderTotals && !isNumeric(grand_total)) {
      errors.grand_total = 'Grand Total must be a number'
      errors.preventSave = true
    }

    return errors
  },
)

function expectedTotalsSelector(state) {
  let {
    orderNumber,
    product_amount,
    discount_amount,
    shipping_amount,
    tax_amount,
    grand_total,
  } = modalFormSelector(state)

  const order = orderSelector(state, {orderNumber})

  const expectedProductAmount = round(
    order.lines.reduce(
      (sum, line) => sum + line.total_price - line.discount_amount,
      0,
    ),
    2,
  )

  product_amount = Number(product_amount)
  shipping_amount = Number(shipping_amount)
  tax_amount = Number(tax_amount)
  discount_amount = Number(discount_amount)
  grand_total = Number(grand_total)

  const expectedGrandTotal = round(
    product_amount + shipping_amount + tax_amount - discount_amount,
    2,
  )

  const outcome = {}

  outcome.product_amount = expectedProductAmount

  if (
    !isNaN(expectedProductAmount) &&
    expectedProductAmount !== product_amount
  ) {
    outcome.hasProductAmountDifferences = true
  }

  outcome.grand_total = expectedGrandTotal

  if (!isNaN(expectedGrandTotal) && expectedGrandTotal !== grand_total) {
    outcome.hasGrandTotalDifferences = true
  }

  return outcome
}

export async function saveOrderTotalsModal() {
  try {
    const {
      orderNumber,
      product_amount,
      discount_amount,
      shipping_amount,
      tax_amount,
      grand_total,
    } = modalFormSelector(getState())
    const autoCalculateOrderTotals =
      autoCalculateOrderTotalsSelector(getState())
    const expectedTotals = expectedTotalsSelector(getState())

    const params = {
      product_amount: autoCalculateOrderTotals
        ? expectedTotals.product_amount
        : Number(product_amount),
      discount_amount: Number(discount_amount),
      shipping_amount: Number(shipping_amount),
      tax_amount: Number(tax_amount),
      grand_total: autoCalculateOrderTotals
        ? expectedTotals.grand_total
        : Number(grand_total),
    }

    updateModalForm({isSaving: true, serverError: null})

    await saveFinancialInfo(orderNumber, params)

    closeModal()

    showMessageToast(`Saved order financial information`)
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function OrderTotalsModalModal({form}) {
  const errors = useSelector(errorsSelector)
  const hasErrors = !isEmpty(errors)
  const expectedTotals = useSelector(expectedTotalsSelector)
  const autoCalculateOrderTotals = useSelector(autoCalculateOrderTotalsSelector)

  return (
    <ConfirmModal
      title="Edit Order Financial Info"
      modalSize="sm"
      onConfirm={() => saveOrderTotalsModal()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={hasErrors}
      error={form.serverError}
    >
      <div className="list__item--form list__item--no-style margin-bottom-20">
        <div className="fs-01">
          <strong>{form.orderNumber}</strong>
        </div>
      </div>
      <ul className="list list--no-style">
        <li className="list__item margin-bottom-25">
          <AutoCalculateOrderTotalsCheckbox />
        </li>
        {autoCalculateOrderTotals ? (
          <li className="list__item fs-00 margin-bottom-15">
            <strong>Product Total:</strong>{' '}
            <div>
              <Currency value={expectedTotals.product_amount} />
            </div>
          </li>
        ) : (
          <li className="list__item margin-bottom-15 flex">
            <div className="margin-right-25">
              <CurrencyInput
                label="Product Total"
                id="product_amount"
                className="biggie margin-bottom-0"
                width="sm"
                value={form.product_amount}
                onChange={(product_amount) => updateModalForm({product_amount})}
                errorMessage={errors.product_amount}
                autoFocus={form.autoFocusOn === 'product_amount'}
              />
            </div>

            {expectedTotals.hasProductAmountDifferences && (
              <div className="alert alert--warning w-60">
                <p className="fs-n0 lh-md margin-bottom-7">
                  Product Total does not match the calculated total of{' '}
                  <strong>
                    <Currency value={expectedTotals.product_amount} />
                  </strong>
                  .
                </p>
                <ButtonPrimary
                  size="xx-sm"
                  isOutlined
                  onClick={() =>
                    updateModalForm({
                      product_amount: expectedTotals.product_amount,
                    })
                  }
                >
                  Use Calculated Total{' '}
                </ButtonPrimary>
              </div>
            )}
          </li>
        )}
        <li className="list__item margin-bottom-15">
          <CurrencyInput
            label="Discount"
            id="discount_amount"
            className="biggie margin-bottom-0"
            width="sm"
            value={form.discount_amount}
            onChange={(discount_amount) => updateModalForm({discount_amount})}
            errorMessage={errors.discount_amount}
            autoFocus={form.autoFocusOn === 'discount_amount'}
          />
        </li>
        <li className="list__item margin-bottom-15">
          <CurrencyInput
            label="Shipping and Handling"
            id="shipping_amount"
            className="biggie margin-bottom-0"
            width="sm"
            value={form.shipping_amount}
            onChange={(shipping_amount) => updateModalForm({shipping_amount})}
            errorMessage={errors.shipping_amount}
            autoFocus={form.autoFocusOn === 'shipping_amount'}
          />
        </li>
        <li className="list__item margin-bottom-15">
          <CurrencyInput
            label="Tax"
            id="tax_amount"
            className="biggie margin-bottom-0"
            width="sm"
            value={form.tax_amount}
            onChange={(tax_amount) => updateModalForm({tax_amount})}
            errorMessage={errors.tax_amount}
            autoFocus={form.autoFocusOn === 'tax_amount'}
          />
        </li>
        {autoCalculateOrderTotals ? (
          <li className="list__item fs-00 margin-top-5 margin-bottom-15">
            <strong>Grand Total:</strong>{' '}
            <div>
              <Currency value={expectedTotals.grand_total} />
            </div>
          </li>
        ) : (
          <li className="list__item margin-bottom-0 flex">
            <div className="margin-right-25">
              <CurrencyInput
                label="Grand Total"
                id="grand_total"
                className="biggie margin-bottom-0"
                width="sm"
                value={form.grand_total}
                onChange={(grand_total) => updateModalForm({grand_total})}
                errorMessage={errors.grand_total}
                autoFocus={form.autoFocusOn === 'grand_total'}
              />
            </div>
            {expectedTotals.hasGrandTotalDifferences && (
              <div className="alert alert--warning w-60">
                <p className="fs-n0 lh-md margin-bottom-7">
                  Grand Total does not match the calculated total of{' '}
                  <strong>
                    <Currency value={expectedTotals.grand_total} />
                  </strong>
                  .
                </p>
                <ButtonPrimary
                  size="xx-sm"
                  isOutlined
                  onClick={() =>
                    updateModalForm({grand_total: expectedTotals.grand_total})
                  }
                >
                  Use Calculated Total{' '}
                </ButtonPrimary>
              </div>
            )}
          </li>
        )}
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(OrderTotalsModalModal, modalFormSelector)
